import { useCallback } from "react"
import { useMutation } from "urql"

import type { CompanySiblingUpdateInput, UpdateSiblingCompanyMutation } from "@/graphql/codegen/graphql"
import { UpdateSiblingCompanyDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export interface UseUpdateSiblingCompaniesReturn {
  updateSiblingCompanies: (input: CompanySiblingUpdateInput) => Promise<boolean>
  isUpdating: boolean
}

export const useUpdateSiblingCompanies = (): UseUpdateSiblingCompaniesReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const [{ fetching }, executeMutation] = useMutation<
    UpdateSiblingCompanyMutation,
    { input: CompanySiblingUpdateInput }
  >(UpdateSiblingCompanyDocument)

  const updateSiblingCompanies = useCallback(
    async (input: CompanySiblingUpdateInput): Promise<boolean> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.companySibling.update

      const hasError = error || output?.__typename === "CompanySiblingUpdateFailure"

      if (hasError) {
        const failureMessage =
          output?.__typename === "CompanySiblingUpdateFailure" ? output.error.message : error?.message

        notify({
          type: NotificationType.Error,
          message: failureMessage ?? "Failed to update sibling companies",
        })
        return false
      }

      notify({
        type: NotificationType.Success,
        message: "Sibling companies updated",
      })
      return true
    },
    [executeMutation, notify]
  )

  return { updateSiblingCompanies, isUpdating: fetching }
}
